/**
 * Estados que indican que la operación se cortó por tiempo y no por un fallo de datos:
 * el cliente dejó de esperar (408) o el proxy dejó de esperar al backend (502/504).
 * Merecen un mensaje propio porque el trabajo puede haberse completado en el servidor y
 * porque la acción correctiva es distinta: reintentar con menos elementos.
 */
const GATEWAY_STATUSES = [408, 502, 504];

/**
 * Códigos con los que axios marca una petición que nunca obtuvo respuesta.
 */
const TIMEOUT_CODES = ["ECONNABORTED", "ETIMEDOUT"];

/**
 * Indica si el error es un corte por tiempo en lugar de una caída de red.
 */
export function isTimeoutError(error: any): boolean {
  return (
    TIMEOUT_CODES.includes(error?.code) ||
    /timeout/i.test(error?.message ?? "")
  );
}

/**
 * Traduce un error de axios a la clave i18n del mensaje que debe verse.
 *
 * Devuelve siempre una clave utilizable: el motivo de existir es que el usuario nunca se
 * quede sin aviso. Antes cada catch inspeccionaba error.response.status directamente, de
 * modo que un error sin respuesta (timeout, red caída, conexión abortada) lanzaba un
 */
export default function httpErrorMessage(error: any): string {
  const response = error?.response;

  // Sin response no hay status ni cuerpo: la petición no llegó a completarse.
  if (!response) {
    return isTimeoutError(error) ? "timeout_error" : "network_error";
  }

  // El servidor web rechaza el cuerpo por tamaño antes de que PHP lo vea.
  if (response.status === 413) {
    return "file_too_large_error";
  }

  if (GATEWAY_STATUSES.includes(response.status)) {
    return "timeout_error";
  }

  // En un 422 el backend sí envía un mensaje aprovechable, pero el cuerpo no siempre es
  // el JSON esperado (puede llegar HTML o vacío), así que solo se usa si es texto.
  if (response.status === 422) {
    const message =
      typeof response.data === "object" && response.data !== null
        ? response.data.message
        : null;
    return typeof message === "string" && message !== ""
      ? message
      : "validation_errors";
  }

  return "server_error";
}
